import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import TabButton from "../components/TabButton";
import { getOperations } from "../services/api-server/operations";
import { getProject } from "../services/api-server/projects";
import { getWells } from "../services/api-server/wells";
import { TOperationData } from "../types/operation";
import { TProjectData } from "../types/project";
import { TWellData } from "../types/well";
import { GetAntIcon } from "../utils/ant_icons";
import { HomeContext } from "./Home";
import MultiAssetPhaseKanbanBoard from "./MultiAssetPhaseKanbanBoard";
import InputSearch from "../components/InputSearch";
import { useMultiAssetStore } from "../stores/multiAssetStore";
import { Button, Flex, Switch } from "antd";
import useFilterCheckbox from "../hooks/useFilterCheckbox";
import CheckboxDropdown from "../components/CheckboxDropdown";

function MultiAssetPhaseContainer({ params }: any) {
	const [activePage, setActivePage] = useState("kanban");
	const [projectData, setProjectData] = useState<TProjectData | null>(null);
	const [operationList, setOperationList] = useState<
		(TOperationData & TWellData)[]
	>([]);

	const { currentTenantKey }: any = useContext(HomeContext);

	const location = useLocation();
	const project_code: any = location?.pathname?.split("/")[2];
	const phase_id: any = location?.pathname?.split("/")[4];

	const {
		setSearchValue,
		selectedFilters,
		setSelectedFilters,
		hideExcluded,
		setHideExcluded,
	} = useMultiAssetStore();
	const { handleMenuClick, handleClearAllFilters } = useFilterCheckbox();

	const [filterMode, setFilterMode] = useState(false);

	useEffect(() => {
		const fetchOperations = async (project_id: string) => {
			try {
				const operations = await getOperations(currentTenantKey, {
					project: project_id,
				});
				for (const operation of operations as TOperationData[] & TWellData[]) {
					const well: any = await getWells(currentTenantKey, {
						_id: operation.well_id,
					});
					operation.well_name = well[0]?.well_name;
				}
				setOperationList(operations as (TOperationData & TWellData)[]);
			} catch (error) {
				console.log(error);
			}
		};

		const fetchProjectData = async (project_code: string) => {
			const projectData: any = await getProject(currentTenantKey, project_code);
			setProjectData(projectData as TProjectData);
			if (projectData?._id) {
				fetchOperations(projectData._id);
			}
		};

		fetchProjectData(project_code);
	}, [currentTenantKey, project_code]);

	const handleFilterChange = (field: string, selectedValues: any[]) => {
		handleMenuClick(field, selectedValues);
		const newFilters = { ...selectedFilters, [field]: selectedValues };
		setSelectedFilters(newFilters);
	};

	const handleClearFilters = () => {
		handleClearAllFilters();
		setSelectedFilters({});
	};

	const projectRoleAssignmentList = projectData?.role_assignments.map(
		(role: any) => {
			return { label: role.role, value: role.role_id };
		}
	);

	return (
		<div className="generic-container">
			<div
				className="project-header"
				style={{ justifyContent: "space-between" }}
			>
				<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
					<TabButton
						identifier="kanban"
						label="Kanban"
						icon={GetAntIcon("project")}
						activePage={activePage}
						setActivePage={setActivePage}
					/>
				</div>
				<Flex gap={"15px"}>
					{activePage === "kanban" && (
						<InputSearch
							placeholder="Search by task name"
							setSearchValue={setSearchValue}
						/>
					)}
					{activePage !== "documents" && (
						<div
							className={filterMode ? "project-active-button" : ""}
							style={{
								padding: "4px 10px",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
							}}
							onClick={() => {
								setFilterMode(!filterMode);
							}}
						>
							{GetAntIcon("filter")}
						</div>
					)}
				</Flex>
			</div>
			<div
				className="generic-content project-overview-container"
				style={{
					flex: 1,
					display: "flex",
					flexWrap: "inherit",
					flexDirection: "column",
					alignItems: "flex-start",
					padding: 0,
					gap: 0,
					overflowX: "auto",
					maxHeight: "100%",
				}}
			>
				<div
					className={`filter-area ${
						filterMode ? "show-filter" : "hide-filter"
					}`}
				>
					{filterMode && (
						<>
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									gap: "10px",
									padding: "8px",
									// marginRight: "20px",
									alignItems: "center",
									height: "40px",
								}}
							>
								Hide excluded items
								<Switch
									defaultValue={hideExcluded}
									onChange={(checked: any) => {
										setHideExcluded(checked);
									}}
								/>
							</div>
							{activePage === "kanban" && (
								<>
									<CheckboxDropdown
										options={projectRoleAssignmentList}
										onChange={(selectedValues) =>
											handleFilterChange("responsible_roles", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.responsible_roles}
										Key="responsible_roles"
										label="Responsible roles"
									/>
									<CheckboxDropdown
										options={projectRoleAssignmentList}
										onChange={(selectedValues) =>
											handleFilterChange("accountable_roles", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.accountable_roles}
										Key="accountable_roles"
										label="Accountable roles"
									/>
									<CheckboxDropdown
										options={projectRoleAssignmentList}
										onChange={(selectedValues) =>
											handleFilterChange("consulted", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.consulted}
										Key="consulted"
										label="Consulted"
									/>
									<CheckboxDropdown
										options={projectRoleAssignmentList}
										onChange={(selectedValues) =>
											handleFilterChange("informed_parties", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.informed_parties}
										Key="informed_parties"
										label="Informed parties"
									/>
									<CheckboxDropdown
										options={[
											{ label: "New", value: "New" },
											{
												label: "To do",
												value: "To do",
											},
											{ label: "In Progress", value: "In progress" },
											{ label: "Late", value: "Late" },
											{
												label: "Resolved",
												value: "Resolved",
											},
										]}
										onChange={(selectedValues) =>
											handleFilterChange("status", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.status}
										Key="status"
										label="Status"
									/>
									<div className="test">
										<Button type="link" onClick={handleClearFilters}>
											Reset All
										</Button>
									</div>
								</>
							)}
						</>
					)}
				</div>
				{activePage == "kanban" && projectData && (
					<MultiAssetPhaseKanbanBoard
						operationList={operationList}
						phase_id={phase_id}
					/>
				)}
			</div>
		</div>
	);
}

export default MultiAssetPhaseContainer;
